import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import VideoCard from "./VideoCard";
import api from "../../../conf/api";

/**
 * Shared "Use Native Video" picker for BOTH publish flows — Brand Publish
 * (BrandPublish/steps/Step2VideoSelection.js) and Push to Admin Portal
 * (PushToAdminPortal/steps/Step3VideoSelection.js). One component, one
 * endpoint (`GET /mrss/native-videos/`), so the filter set never drifts
 * between the two screens.
 *
 * The backend (MRSSManagementApp/native_video_filters.py) does all of the
 * filtering and hard-caps the response at 10 rows, returning the true match
 * count alongside them — that is what the "showing top N of M" hint below
 * reports. It also validates every param, so a half-typed date is a 400 with
 * a readable message rather than a 500.
 */

// Mirrors native_video_filters.MAX_RESULTS. Display only — the real cap is
// enforced server-side; this is just what the empty-filter hint promises.
const MAX_RESULTS = 10;

const EMPTY_FILTERS = {
  search: "",
  creator: "",
  wgc: "",
  title: "",
  category: "",
  date_field: "created",
  date_from: "",
  date_to: "",
  duration_min: "",
  duration_max: "",
  muted: "",
  claimed: "",
  wgc_minted: "",
  queue_status: "",
  sort: "newest",
};

const DATE_FIELD_OPTIONS = [
  { value: "created", label: "Uploaded to library" },
  { value: "uploaded", label: "Dropbox upload date" },
  { value: "approved", label: "Approval date" },
];

// VideoManagerApp.ThreadStatus — the async Dropbox pipeline's state.
const QUEUE_STATUS_OPTIONS = ["Pending", "INQUEUE", "Running", "Completed", "Failed"];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "approved", label: "Recently approved" },
  { value: "title", label: "Title A–Z" },
  { value: "-title", label: "Title Z–A" },
  { value: "duration", label: "Shortest first" },
  { value: "-duration", label: "Longest first" },
];

const TRI_STATE = [
  { value: "", label: "Any" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

/** Only send params the user actually set — keeps the URL and the SQL small. */
function toParams(filters) {
  const params = {};
  Object.entries(filters).forEach(([key, value]) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed === "" || trimmed === null || trimmed === undefined) return;
    // Defaults carry no information — the backend applies them anyway.
    if (key === "date_field" && trimmed === EMPTY_FILTERS.date_field) return;
    if (key === "sort" && trimmed === EMPTY_FILTERS.sort) return;
    params[key] = trimmed;
  });
  return params;
}

function formatDuration(seconds) {
  const total = Math.round(Number(seconds) || 0);
  if (!total) return null;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

export default function NativeVideoPicker({
  selectedKeys = [],
  onSelect,
  emptyMessage = "No approved native videos yet — approve one in the Video Library and wait for its upload to finish processing first.",
}) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [videos, setVideos] = useState([]);
  const [count, setCount] = useState(0);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guards against an older, slower response overwriting a newer one.
  const requestSeq = useRef(0);

  const params = useMemo(() => toParams(filters), [filters]);
  const paramsKey = JSON.stringify(params);

  const fetchVideos = useCallback(async (queryParams) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/mrss/native-videos/", { params: queryParams });
      if (seq !== requestSeq.current) return;
      const data = res.data || {};
      const list = Array.isArray(data) ? data : data.results || [];
      setVideos(list);
      setCount(Array.isArray(data) ? list.length : data.count ?? list.length);
      setCapped(Array.isArray(data) ? false : !!data.capped);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setVideos([]);
      setCount(0);
      setCapped(false);
      setError(
        err?.response?.data?.detail ||
          "Couldn't load native videos. Adjust the filters and try again."
      );
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  // Debounced so typing in a text filter doesn't fire a request per keystroke.
  useEffect(() => {
    const parsed = JSON.parse(paramsKey);
    const timer = setTimeout(() => fetchVideos(parsed), 350);
    return () => clearTimeout(timer);
  }, [paramsKey, fetchVideos]);

  const setFilter = (name) => (event) =>
    setFilters((prev) => ({ ...prev, [name]: event.target.value }));

  const activeFilterCount = Object.keys(params).filter((k) => k !== "search").length;
  const hasAnyFilter = Object.keys(params).length > 0;

  const textField = (name, label, extra = {}) => (
    <TextField
      size="small"
      fullWidth
      label={label}
      value={filters[name]}
      onChange={setFilter(name)}
      {...extra}
    />
  );

  const selectField = (name, label, options, extra = {}) => (
    <TextField
      select
      size="small"
      fullWidth
      label={label}
      value={filters[name]}
      onChange={setFilter(name)}
      {...extra}
    >
      {options.map((opt) => {
        const value = typeof opt === "string" ? opt : opt.value;
        const text = typeof opt === "string" ? opt : opt.label;
        return (
          <MenuItem key={`${name}-${value}`} value={value}>
            {text}
          </MenuItem>
        );
      })}
    </TextField>
  );

  return (
    <Box>
      {/* Primary row: one smart search box + the two filters people ask for
          most (creator, WGC id), then everything else behind "More filters". */}
      <Grid container spacing={1} alignItems="center" mb={1}>
        <Grid item xs={12} md={5}>
          <TextField
            size="small"
            fullWidth
            label="Search"
            placeholder="Title, WGC id, WGA creator id, name or email"
            value={filters.search}
            onChange={setFilter("search")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: filters.search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setFilters((p) => ({ ...p, search: "" }))}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {textField("creator", "Creator", { placeholder: "Name, email or WGA id" })}
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          {textField("wgc", "WGC id", { placeholder: "WGC12345" })}
        </Grid>
        <Grid item xs={12} md={2}>
          <Box display="flex" gap={0.5}>
            <Button
              size="small"
              variant={showAdvanced ? "contained" : "outlined"}
              startIcon={<TuneIcon fontSize="small" />}
              onClick={() => setShowAdvanced((s) => !s)}
              fullWidth
            >
              Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
            <Tooltip title="Refresh results">
              <span>
                <IconButton size="small" onClick={() => fetchVideos(params)} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      <Collapse in={showAdvanced} unmountOnExit>
        <Grid container spacing={1} mb={1}>
          <Grid item xs={12} sm={6} md={3}>
            {textField("title", "Title contains")}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {textField("category", "Creator category")}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField("date_field", "Date field", DATE_FIELD_OPTIONS)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {selectField("sort", "Sort by", SORT_OPTIONS)}
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            {textField("date_from", "From", {
              type: "date",
              InputLabelProps: { shrink: true },
            })}
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            {textField("date_to", "To", {
              type: "date",
              InputLabelProps: { shrink: true },
            })}
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            {textField("duration_min", "Min secs", {
              type: "number",
              inputProps: { min: 0, step: 1 },
            })}
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            {textField("duration_max", "Max secs", {
              type: "number",
              inputProps: { min: 0, step: 1 },
            })}
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            {selectField("queue_status", "Processing status", [
              { value: "", label: "Any" },
              ...QUEUE_STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
            ])}
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            {selectField("muted", "Muted", TRI_STATE)}
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            {selectField("claimed", "Claimed", TRI_STATE)}
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            {selectField("wgc_minted", "WGC id minted", TRI_STATE)}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              size="small"
              variant="text"
              disabled={!hasAnyFilter}
              onClick={() => setFilters(EMPTY_FILTERS)}
            >
              Clear all filters
            </Button>
          </Grid>
        </Grid>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
      ) : videos.length === 0 ? (
        <Alert severity="info">
          {hasAnyFilter
            ? "No native videos match these filters — try widening the date range or clearing a field."
            : emptyMessage}
        </Alert>
      ) : (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Click a native video to add it:
            </Typography>
            <Typography variant="caption" color={capped ? "warning.main" : "text.secondary"}>
              {capped
                ? `Showing top ${videos.length} of ${count} matches — refine your search`
                : `Showing ${videos.length} of ${count} match${count === 1 ? "" : "es"}`}
            </Typography>
          </Box>

          <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))" }}>
            {videos.map((nv) => {
              const already = selectedKeys.includes(nv.unique_key);
              const duration = formatDuration(nv.duration);
              return (
                <Box key={nv.unique_key || nv.native_video_id} sx={{ minWidth: 0 }}>
                  <Box
                    onClick={() =>
                      !already &&
                      onSelect({
                        unique_key: nv.unique_key,
                        title: nv.title,
                        thumbnail_url: nv.thumbnail_url,
                        s3_url: nv.s3_url,
                        is_native_video: true,
                        native_video_id: nv.native_video_id,
                      })
                    }
                    sx={{
                      cursor: already ? "default" : "pointer",
                      opacity: already ? 0.45 : 1,
                      border: "2px solid",
                      borderColor: already ? "success.main" : "transparent",
                      borderRadius: 1,
                    }}
                  >
                    <VideoCard video={nv} />
                    <Box display="flex" flexWrap="wrap" gap={0.5} px={0.5} pb={0.5}>
                      {nv.creator_name && (
                        <Tooltip title={nv.creator_email || nv.creator_id || ""}>
                          <Chip size="small" variant="outlined" label={nv.creator_name} />
                        </Tooltip>
                      )}
                      {duration && <Chip size="small" variant="outlined" label={duration} />}
                      {nv.muted && (
                        <Chip size="small" variant="outlined" color="warning" label="Muted" />
                      )}
                      {nv.claimed && (
                        <Chip size="small" variant="outlined" color="info" label="Claimed" />
                      )}
                      {nv.wgc_minted === false && (
                        <Tooltip title="WGC id is reserved but its AssetsSheet row hasn't been minted yet — still publishable.">
                          <Chip size="small" variant="outlined" label="WGC pending" />
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {capped && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Only the top {MAX_RESULTS} matches are loaded. Narrow by creator, WGC id or date
              to see the one you need.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
